import React from "react";
import { Building2 } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

import {
  selectCurrentOccupancy,
  selectManagementOccupancy,
  windowLabel,
} from "./overviewSelectors";
import { OCCUPANCY_FRACTION_DIGITS } from "../../../utils/occupancyPercent";

/**
 * "Auslastung" — the occupancy tile, on three levels.
 *
 * TWO AXES IN ONE CARD, AND THAT IS THE WHOLE POINT.
 * The headline is TODAY's portfolio rate (`/occupancy`, KPI C.1, current state) and
 * the line below it is the MANAGEMENT occupancy for the selected period
 * (`/occupancy/room-days`, KPI C.4). Until B1e this card showed only the period
 * figure, which is what a period header explains but not what anybody means by "how
 * full are we". Showing both is only honest because each says which axis it is on —
 * "Aktuell belegt" against "April – September 2026" — so neither can be read as the
 * other, and a period change moves exactly one of them.
 *
 * THE PERIOD LINE CHANGED CONTRACT IN P3b, AND SAYS SO.
 * It used to read `/occupancy/average` — KPI C.2, the unweighted mean of the daily
 * rates — under an "Ø" prefix. C.4 is the ratio of the room-day sums, which is the
 * figure a management view answers a period with and the same family the Performance
 * tab divides. They are not the same number: February at 100 % and August at 0 % is
 * 47.5 % as a ratio of sums and 50 % as a mean of rates, and both are correct for the
 * question they were asked. So the source moved AND the "Ø" went with it — keeping
 * the average's prefix over a ratio would have relabelled C.2 rather than replaced
 * it. C.2 itself is untouched and keeps its own endpoint and its own meaning.
 *
 * WHY THIS IS NOT `OverviewKpiCard`.
 * That component has ONE source: when its `error` is set it replaces the subtext and
 * hides the footer. Here the two halves fail independently, and a failed snapshot must
 * still leave the month's average on screen (and the other way round). Bending the
 * shared card into two error states would change a component seven other tiles use.
 *
 * NO ARITHMETIC. Every figure is printed as the contract states it: the rate is
 * already a percentage, the counts are already counts, and `reserved_rooms` is shown
 * beside `occupied_rooms` rather than added to it — the backend's occupancy is
 * occupied/total, and a second definition invented here is how two screens end up
 * disagreeing about how full the same building is.
 */

const CARD =
  "flex h-full flex-col rounded-xl border border-border bg-card p-4 shadow-sm transition-colors hover:border-border/80";

// The same violet the tile carried before B1e, as a light/dark PAIR: a single
// light-only value is a bright block on the dark page.
const CHIP =
  "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-violet-50 text-violet-600 dark:bg-violet-500/10 dark:text-violet-400";

const RETRY =
  "rounded-md border border-border px-2 py-0.5 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background";

/**
 * A contract percentage → text. One decimal at most, so a portfolio that is exactly
 * full reads "100 %" rather than "100.0 %", and a corrected figure reads "99.1 %"
 * rather than "99.08 %".
 *
 * No `* 100`: both `summary.occupancy_rate` (C.1) and `occupancy_pct` (C.4) are
 * already percentages. A non-breaking space keeps the number and its sign together.
 */
const fmtPercent = (value) =>
  value === null || value === undefined
    ? null
    : `${new Intl.NumberFormat("de-CH", { maximumFractionDigits: 1 }).format(
        value
      )} %`;

/**
 * TODAY's rate, on the OC3 contract: ALWAYS one decimal, so a full portfolio
 * reads "100.0 %" and not "100 %".
 *
 * WHY A SECOND FORMATTER RATHER THAN A WIDER ONE. `fmtPercent` above also writes
 * the PERIOD line, which is KPI C.4 (the ratio of room-day sums over the selected
 * window) -- a different axis, not current occupancy, and not this work's to
 * change. Widening the shared one would have moved both.
 *
 * WHY STILL `Intl` AND NOT THE CONTRACT'S OWN STRING. Two reasons, and the
 * second is the load-bearing one. The card writes a NON-BREAKING SPACE before
 * the sign ("66.7 %") where the contract writes none ("66.7%"), and settling
 * that difference is OC4's. And `Intl` and `toFixed` disagree on a tie --
 * 0.15 rounds to "0.2" here and to "0.1" there -- so taking the contract's
 * string would have quietly changed this card's rounding as well as its
 * precision. Only the DIGIT COUNT comes from the contract; everything else
 * about this card's number is exactly what it was.
 */
const fmtCurrentPercent = (value) =>
  value === null || value === undefined
    ? null
    : `${new Intl.NumberFormat("de-CH", {
        minimumFractionDigits: OCCUPANCY_FRACTION_DIGITS,
        maximumFractionDigits: OCCUPANCY_FRACTION_DIGITS,
      }).format(value)} %`;

/** Dative plural, so a one-room portfolio does not read "1 von 1 Zimmern". */
const rooms = (n) => (n === 1 ? "Zimmer" : "Zimmern");

function RetryButton({ onRetry }) {
  if (!onRetry) return null;
  return (
    <button type="button" onClick={onRetry} className={RETRY}>
      Erneut laden
    </button>
  );
}

export default function OverviewOccupancyCard({
  currentOccupancy,
  occupancyRoomDays,
  window: periodWindow,
  unsupported = false,
  currentLoading = false,
  periodicLoading = false,
  onRetryCurrent,
  onRetryPeriodic,
}) {
  const currentError = currentOccupancy?.error;
  // An unsupported period is not a failed request: nothing was asked for, so there is
  // nothing to retry and nothing to call unavailable.
  const periodError = unsupported ? "" : occupancyRoomDays?.error;

  const snapshot = selectCurrentOccupancy(currentOccupancy?.data);
  const periodRate = unsupported
    ? null
    : selectManagementOccupancy(occupancyRoomDays?.data).rate;

  const {
    rate,
    totalRooms,
    occupiedRooms,
    reservedRooms,
    freeRooms,
    activeUnits,
    hasRooms,
    hasPortfolio,
  } = snapshot;

  // The room ratio, which is what "belegt" actually means here. A portfolio with no
  // active rooms says so instead of claiming a ratio out of nothing.
  const roomsLine = hasRooms
    ? `Aktuell belegt · ${occupiedRooms ?? "—"} von ${totalRooms} ${rooms(totalRooms)}`
    : totalRooms === 0
    ? "Aktuell keine Zimmer im Portfolio"
    : "Aktueller Stand";

  // Response-driven, every one of them. Nothing here is a literal count, so a fifth
  // unit going live in October changes this line without a code change.
  const facts = [];
  if (activeUnits !== null) {
    facts.push({
      key: "units",
      text: `${activeUnits} ${activeUnits === 1 ? "aktive Einheit" : "aktive Einheiten"}`,
    });
  }
  if (totalRooms !== null) facts.push({ key: "rooms", text: `${totalRooms} Zimmer` });
  if (occupiedRooms !== null) {
    facts.push({ key: "occupied", text: `${occupiedRooms} belegt` });
  }
  if (freeRooms !== null) facts.push({ key: "free", text: `${freeRooms} frei` });
  // Shown only when there is something reserved. At zero it is not a fact worth the
  // space, and it is NEVER folded into "belegt" — a reserved room is not an occupied
  // one, which is exactly why the contract counts them apart.
  if (reservedRooms !== null && reservedRooms > 0) {
    facts.push({ key: "reserved", text: `${reservedRooms} reserviert` });
  }

  return (
    <div
      data-testid="kpi-occupancy"
      aria-busy={currentLoading || periodicLoading || undefined}
      className={CARD}
    >
      <div className="flex items-start gap-2.5">
        <span aria-hidden className={CHIP}>
          <Building2 className="h-4 w-4" />
        </span>
        <span className="mt-1 text-[11px] font-medium leading-tight text-muted-foreground">
          Auslastung
        </span>
      </div>

      {/* PRIMARY — today's rate. `—` when it is unknown: a failed or empty snapshot
          rendered as "0 %" would claim every room is empty. */}
      <div className="mt-3">
        {currentLoading ? (
          <Skeleton className="h-6 w-24" />
        ) : (
          <div
            data-testid="occupancy-rate"
            className="text-[20px] font-semibold tracking-tight text-card-foreground"
          >
            {fmtCurrentPercent(rate) ?? "—"}
          </div>
        )}
      </div>

      {/* SECONDARY — the ratio behind that percentage, or this half's own error. */}
      {currentLoading ? null : currentError ? (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {/* Text, not colour alone. */}
          <span className="text-[11px] text-destructive">Nicht verfügbar</span>
          <RetryButton onRetry={onRetryCurrent} />
        </div>
      ) : (
        <div
          data-testid="occupancy-rooms"
          className="mt-2 text-[11px] leading-snug text-muted-foreground"
        >
          {roomsLine}
        </div>
      )}

      {/* CONTEXT — the selected period, deliberately below the current figure and
          smaller than it. Its own source, so it survives a failed snapshot and vice
          versa. The label is the window itself; `windowLabel` collapses an equal pair
          to one month, so a single-month selection reads exactly as it always did. */}
      <div className="mt-3 border-t border-border pt-2 text-[11px] leading-snug">
        <div
          data-testid="occupancy-period"
          className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1"
        >
          <span className="text-muted-foreground">
            {unsupported ? "Zeitraum wählen" : windowLabel(periodWindow) || "—"}
          </span>
          {periodicLoading && !unsupported ? (
            <Skeleton className="h-3 w-12" />
          ) : periodError ? (
            <span className="flex flex-wrap items-center gap-2">
              <span className="text-destructive">Nicht verfügbar</span>
              <RetryButton onRetry={onRetryPeriodic} />
            </span>
          ) : (
            <span className="font-medium text-card-foreground">
              {fmtPercent(periodRate) ?? "—"}
            </span>
          )}
        </div>

        {/* PORTFOLIO — what the rate is a rate OF. Wraps rather than truncates, so a
            narrow screen gets two lines instead of a cut-off one. */}
        {!currentLoading && !currentError && hasPortfolio && facts.length > 0 ? (
          <ul
            data-testid="occupancy-portfolio"
            className="mt-1.5 flex list-none flex-wrap items-center gap-x-1.5 gap-y-0.5 p-0 text-muted-foreground"
          >
            {facts.map((fact) => (
              // The separator is a pseudo-element, so each fact is ONE element with
              // exactly its own text — a middot in the markup would put the same
              // string on both the item and its inner span.
              <li
                key={fact.key}
                className="before:mr-1.5 before:content-['·'] first:before:content-none"
              >
                {fact.text}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </div>
  );
}
